import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Leaf, Copy, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      setResult(r.data);
    } catch (e) {
      toast.error("Could not start reset");
    } finally {
      setBusy(false);
    }
  };

  const resetLink = result?.token ? `${window.location.origin}/reset-password?token=${result.token}` : "";

  const copy = async () => {
    await navigator.clipboard.writeText(resetLink);
    toast.success("Link copied");
  };

  const emailSent = result?.email_sent;
  const hasToken = !!result?.token;

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
      <Card className="w-full max-w-md rounded-3xl border-emerald-100 p-8">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-sky-400 flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-bold">NutriGuide</span>
        </Link>

        <h1 className="font-display text-2xl font-bold text-slate-900">Reset your password</h1>
        <p className="text-sm text-slate-500 mt-1">Enter your account email and we&apos;ll send a reset link.</p>

        {!result && (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required className="rounded-xl h-11" value={email}
                     onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="forgot-submit">
              {busy ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}

        {result && emailSent && !hasToken && (
          <div className="mt-6 space-y-4" data-testid="forgot-success">
            <div className="flex items-center gap-2 text-emerald-700">
              <Mail className="w-5 h-5" />
              <span className="text-sm font-medium">Check your inbox</span>
            </div>
            <p className="text-sm text-slate-600">
              If that email is on file, we&apos;ve sent a reset link. It expires in 30 minutes.
              Check your spam folder if you don&apos;t see it.
            </p>
          </div>
        )}

        {result && !emailSent && !hasToken && (
          <div className="mt-6 space-y-4" data-testid="forgot-success">
            <div className="flex items-center gap-2 text-emerald-700">
              <Mail className="w-5 h-5" />
              <span className="text-sm font-medium">Check your inbox</span>
            </div>
            <p className="text-sm text-slate-600">
              {result.message || "If that email is on file, we've sent a reset link."}
              &nbsp;It expires in 30 minutes.
            </p>
          </div>
        )}

        {result && hasToken && (
          <div className="mt-6 space-y-4" data-testid="forgot-success">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-sm font-medium">Reset link ready</span>
            </div>
            <p className="text-sm text-slate-600">
              {emailSent
                ? "We emailed you a link. As a backup you can use this one directly (expires in 30 min):"
                : "Couldn't email your reset link right now — use this one-time link instead (expires in 30 min):"}
            </p>
            <div className="bg-emerald-50 rounded-xl p-3 text-xs text-slate-700 break-all border border-emerald-100" data-testid="forgot-link">
              {resetLink}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full flex-1" onClick={copy} data-testid="forgot-copy">
                <Copy className="w-4 h-4 mr-2" /> Copy
              </Button>
              <Link to={`/reset-password?token=${result.token}`} className="flex-1">
                <Button className="rounded-full w-full bg-emerald-600 hover:bg-emerald-700" data-testid="forgot-open">
                  Open link
                </Button>
              </Link>
            </div>
          </div>
        )}

        <div className="mt-6 text-sm text-slate-500 text-center">
          <Link to="/login" className="text-emerald-700 font-medium hover:underline">Back to sign in</Link>
        </div>
      </Card>
    </div>
  );
}
